'use client';

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface TocItem {
  id: string;
  label: string;
}

interface TableOfContentsProps {
  items: TocItem[];
}

export function TableOfContents({ items }: TableOfContentsProps) {
  const [open, setOpen] = useState(false);

  // Auto-hide when the page has fewer than 3 headings (plan 54-02 D-14).
  if (items.length < 3) return null;

  return (
    <section id="toc" className="border-t border-gray-100 bg-gray-50 px-4 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto max-w-3xl">
        {/* Desktop heading — always visible on md+ */}
        <h2 className="hidden text-lg font-bold text-gray-900 md:block">
          In this guide
        </h2>

        {/* Mobile toggle button — visible below md */}
        <button
          type="button"
          className="flex w-full items-center justify-between text-lg font-bold text-gray-900 md:hidden"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
        >
          <span>In this guide</span>
          <ChevronDown
            className={`h-5 w-5 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Desktop nav — always visible on md+ */}
        <nav className="mt-4 hidden md:block">
          <ol className="space-y-2 text-brand-700">
            {items.map((item, i) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className="hover:text-accent-dark underline"
                >
                  {i + 1}. {item.label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Mobile nav — toggled by button */}
        {open && (
          <nav className="mt-4 md:hidden">
            <ol className="space-y-2 text-brand-700">
              {items.map((item, i) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className="hover:text-accent-dark underline"
                  >
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}
      </div>
    </section>
  );
}
